import { useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { UserRole } from './types';
import { useAuth } from './hooks/useAuth';
import { AppRouter } from './components/core/AppRouter';
import { Toaster } from './components/ui/sonner';
import { routeConfig } from './routes';
import { NotificationProvider } from './components/context/NotificationContext';
import { SearchProvider } from './components/context/SearchContext';
import { requestNotificationPermission } from './utils/notificationUtils';
import { initializePortalAuth } from './utils/portalAuth';

export default function App() {
  const { currentUser, login, isLoading, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    requestNotificationPermission();
    
    // Initialize portal authentication
    const { switchSuccess } = initializePortalAuth();
    if (switchSuccess) {
      console.log('Portal switch authentication successful');
    }
  }, []);

  // Auto-redirect to dashboard when user is authenticated
  useEffect(() => {
    if (!isLoading && isAuthenticated && currentUser) {
      console.log(`User authenticated: ${currentUser.name} (${currentUser.role})`);
      // Always show the role-based dashboard for authenticated users
      if (location.pathname === '/' || location.pathname === routeConfig.login) {
        console.log(`Auto-redirecting ${currentUser.role} user to dashboard`);
        navigate(routeConfig.dashboard, { replace: true });
      }
    } else if (!isLoading && !isAuthenticated) {
      // If not authenticated, redirect to parent site login
      console.log('User not authenticated, redirecting to parent site login');
      console.log('Current location:', location.pathname);
      console.log('Current URL:', window.location.href);

      if (location.pathname !== '/test-integration') {
        console.log(`Redirecting to parent site login: ${import.meta.env.VITE_HRMS_FRONTEND_URL}/login`);
        window.location.href = `${import.meta.env.VITE_HRMS_FRONTEND_URL}/login`
        return;
      }
    }
  }, [isLoading, isAuthenticated, currentUser, location.pathname, navigate]);

  const handleLogin = async (email: string, password: string) => {
    const success = await login(email, password);
    if (success) {
      navigate(routeConfig.dashboard);
    }
    return success;
  };



  // Show loading screen while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Connecting to backend...</p>
          <p className="mt-2 text-sm text-gray-500">Checking authentication status...</p>
          <div className="mt-2 text-xs text-gray-400">
            URL: {window.location.href}
          </div>
        </div>
      </div>
    );
  }

  // If authenticated, show existing dashboard components
  if (isAuthenticated && currentUser) {
    return (
      <SearchProvider>
        <NotificationProvider>

          <AppRouter
            currentUser={currentUser}
            onLogin={handleLogin}
          />
          <Toaster />

        </NotificationProvider>
      </SearchProvider>
    );
  }

  // If not authenticated, show simple login
  return (
    <>
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white p-8 rounded-lg shadow-md">
          <h1 className="text-2xl font-bold text-center mb-6">Onboarding Platform</h1>
          <div className="bg-blue-50 p-4 rounded-lg mb-6">
            <p className="text-sm text-blue-800">
              Please login with your credentials. The system will automatically detect your role and redirect you to the appropriate dashboard.
            </p>
          </div>
          <SimpleLoginForm onLogin={handleLogin} />
        </div>
      </div>
      <Toaster />
    </>
  );
}

// Simple inline login form
const SimpleLoginForm = ({ onLogin }: { onLogin: (email: string, password: string) => Promise<boolean> }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const success = await onLogin(email, password);
      if (!success) {
        setError('Login failed. Please check your credentials.');
      }
    } catch (err: any) {
      setError(err.message || 'Login error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
            Email
          </label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter your email"
          />
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
            Password
          </label>
          <input
            type="password"
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter your password"
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-blue-500 text-white py-2 px-4 rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Logging in...' : 'Login'}
        </button>
      </form>

      <div className="mt-6 text-center">
        <a
          href="/test-integration"
          className="text-sm text-blue-600 hover:underline"
        >
          Test Integration Page
        </a>
      </div>
    </>
  );
};